/**
 * Paste Event Handler
 * 
 * Handles copy/paste events with automatic formatting and validation.
 * Processes clipboard data and applies appropriate formatting while
 * maintaining cursor position and user experience.
 */

import { FieldType } from '../../detection-patterns';
import { EventContext, EventProcessingResult, EventHandler } from '../event-manager';

/**
 * Paste processing configuration
 */
export interface PasteProcessingConfig {
  /** Whether to automatically format pasted content */
  autoFormat: boolean;
  /** Whether to validate pasted content */
  validateOnPaste: boolean;
  /** Whether to allow multi-line paste (for textarea fields) */
  allowMultiLine: boolean;
  /** Maximum length for pasted content */
  maxLength?: number;
  /** Whether to strip invalid characters */
  stripInvalidChars: boolean;
  /** Whether to preserve partial valid data */
  preservePartialData: boolean;
  /** Custom paste processors for specific data patterns */
  customProcessors?: PasteDataProcessor[];
}

/**
 * Paste data processor function
 */
export interface PasteDataProcessor {
  /** Name/identifier for this processor */
  name: string;
  /** Pattern to detect if this processor should handle the data */
  pattern: RegExp;
  /** Priority (higher = earlier execution) */
  priority: number;
  /** Function to process the paste data */
  process: (data: string, fieldType: FieldType) => PasteProcessingResult;
}

/**
 * Result of paste data processing
 */
export interface PasteProcessingResult {
  /** Whether processing was successful */
  success: boolean;
  /** Processed paste data */
  processedData: string;
  /** Detected data type */
  detectedType?: string;
  /** Whether formatting was applied */
  formatApplied?: boolean;
  /** Any errors or warnings */
  errors?: string[];
  /** Whether to prevent the default paste behavior */
  preventDefault?: boolean;
}

/**
 * Default configurations for different field types
 */
const DEFAULT_PASTE_CONFIGS: Record<FieldType, PasteProcessingConfig> = {
  [FieldType.PHONE]: {
    autoFormat: true,
    validateOnPaste: true,
    allowMultiLine: false,
    maxLength: 20,
    stripInvalidChars: true,
    preservePartialData: true,
    customProcessors: []
  },
  [FieldType.ZIP]: {
    autoFormat: true,
    validateOnPaste: true,
    allowMultiLine: false,
    maxLength: 10,
    stripInvalidChars: true,
    preservePartialData: true,
    customProcessors: []
  },
  [FieldType.DATE_OF_BIRTH]: {
    autoFormat: true,
    validateOnPaste: true,
    allowMultiLine: false,
    maxLength: 10,
    stripInvalidChars: true,
    preservePartialData: true,
    customProcessors: []
  },
  [FieldType.CURRENCY]: {
    autoFormat: true,
    validateOnPaste: true,
    allowMultiLine: false,
    maxLength: 15,
    stripInvalidChars: true,
    preservePartialData: true,
    customProcessors: []
  },
  [FieldType.SSN]: {
    autoFormat: false, // SSN formatting only on blur for security
    validateOnPaste: false,
    allowMultiLine: false,
    maxLength: 11,
    stripInvalidChars: true,
    preservePartialData: false, // All or nothing for SSN
    customProcessors: []
  },
  [FieldType.NAME]: {
    autoFormat: true,
    validateOnPaste: true,
    allowMultiLine: false,
    maxLength: 100,
    stripInvalidChars: true,
    preservePartialData: true,
    customProcessors: []
  },
  [FieldType.UNKNOWN]: {
    autoFormat: false,
    validateOnPaste: false,
    allowMultiLine: true,
    stripInvalidChars: false,
    preservePartialData: true,
    customProcessors: []
  }
};

/**
 * Built-in paste data processors
 */
const BUILT_IN_PROCESSORS: PasteDataProcessor[] = [
  // Phone number processor
  {
    name: 'phone-international',
    pattern: /^\+?1?[-.\s]?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/,
    priority: 10,
    process: (data: string, fieldType: FieldType) => {
      if (fieldType !== FieldType.PHONE) {
        return { success: false, processedData: data };
      }

      const digits = data.replace(/\D/g, '');
      if (digits.length === 11 && digits.startsWith('1')) {
        // US number with country code
        const processedData = digits.substring(1);
        return {
          success: true,
          processedData,
          detectedType: 'us-phone-with-country-code',
          formatApplied: false // Will be formatted by input handler
        };
      } else if (digits.length === 10) {
        return {
          success: true,
          processedData: digits,
          detectedType: 'us-phone',
          formatApplied: false
        };
      }

      return { success: false, processedData: data };
    }
  },

  // Credit card processor (might be pasted into currency fields)
  {
    name: 'credit-card-number',
    pattern: /^(\d{4}[-.\s]?){3}\d{4}$/,
    priority: 8,
    process: (data: string, fieldType: FieldType) => {
      // Only process if this looks like a credit card but field expects something else
      const digits = data.replace(/\D/g, '');
      if (digits.length === 16 && fieldType !== FieldType.CURRENCY) {
        return {
          success: false,
          processedData: data,
          errors: ['Credit card numbers should not be pasted into this field type']
        };
      }
      return { success: false, processedData: data };
    }
  },

  // Date processor
  {
    name: 'date-various-formats',
    pattern: /^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/,
    priority: 9,
    process: (data: string, fieldType: FieldType) => {
      if (fieldType !== FieldType.DATE_OF_BIRTH) {
        return { success: false, processedData: data };
      }

      const match = data.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
      if (match) {
        const [, month, day, year] = match;
        const fullYear = year.length === 2 ? `20${year}` : year;
        const processedData = `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${fullYear}`;
        
        return {
          success: true,
          processedData,
          detectedType: 'date-formatted',
          formatApplied: true
        };
      }

      return { success: false, processedData: data };
    }
  },

  // Currency processor
  {
    name: 'currency-various-formats',
    pattern: /^[\$]?(\d{1,3}(,\d{3})*(\.\d{0,2})?|\d+(\.\d{0,2})?)$/,
    priority: 9,
    process: (data: string, fieldType: FieldType) => {
      if (fieldType !== FieldType.CURRENCY) {
        return { success: false, processedData: data };
      }

      // Extract numeric value
      const numericValue = data.replace(/[^\d.]/g, '');
      const num = parseFloat(numericValue);
      
      if (!isNaN(num)) {
        return {
          success: true,
          processedData: numericValue,
          detectedType: 'currency-amount',
          formatApplied: false // Will be formatted by input handler
        };
      }

      return { success: false, processedData: data };
    }
  },

  // ZIP code processor
  {
    name: 'zip-code',
    pattern: /^(\d{5})(-?\d{4})?$/,
    priority: 9,
    process: (data: string, fieldType: FieldType) => {
      if (fieldType !== FieldType.ZIP) {
        return { success: false, processedData: data };
      }

      const match = data.match(/^(\d{5})(-?\d{4})?$/);
      if (match) {
        const [, zip5, zip4] = match;
        const processedData = zip4 ? `${zip5}${zip4}` : zip5;
        
        return {
          success: true,
          processedData,
          detectedType: zip4 ? 'zip-plus-4' : 'zip-5',
          formatApplied: false
        };
      }

      return { success: false, processedData: data };
    }
  },

  // SSN processor (strict)
  {
    name: 'ssn-format',
    pattern: /^(\d{3})-?(\d{2})-?(\d{4})$/,
    priority: 10,
    process: (data: string, fieldType: FieldType) => {
      if (fieldType !== FieldType.SSN) {
        return { success: false, processedData: data };
      }

      const digits = data.replace(/\D/g, '');
      if (digits.length === 9) {
        return {
          success: true,
          processedData: digits,
          detectedType: 'ssn',
          formatApplied: false // SSN formatting only on blur
        };
      }

      return { success: false, processedData: data, errors: ['Invalid SSN format'] };
    }
  }
];

/**
 * Create a paste event handler for a specific field type
 * 
 * @param fieldType The type of field to handle
 * @param customConfig Optional configuration overrides
 * @returns Event handler function
 */
export function createPasteHandler(
  fieldType: FieldType,
  customConfig?: Partial<PasteProcessingConfig>
): EventHandler {
  const config = { ...DEFAULT_PASTE_CONFIGS[fieldType], ...customConfig };
  const processors = [...BUILT_IN_PROCESSORS, ...(config.customProcessors || [])];

  return async (context: EventContext): Promise<EventProcessingResult> => {
    const startTime = performance.now();
    const { event, element, field } = context;
    
    // Ensure this is actually a paste event
    if (!(event instanceof ClipboardEvent) || event.type !== 'paste') {
      return {
        success: false,
        errors: ['Not a paste event'],
        processingTime: performance.now() - startTime
      };
    }

    try {
      // Get clipboard data
      const clipboardData = event.clipboardData;
      if (!clipboardData) {
        return {
          success: false,
          errors: ['No clipboard data available'],
          processingTime: performance.now() - startTime
        };
      }

      // Get the pasted text
      const pastedText = clipboardData.getData('text/plain') || '';
      if (pastedText.length === 0) {
        return {
          success: true,
          processingTime: performance.now() - startTime
        };
      }

      // Process the paste data
      const processingResult = await processPasteData(pastedText, fieldType, config, processors);
      
      // Handle processing result
      if (!processingResult.success) {
        if (config.preservePartialData && processingResult.processedData !== pastedText) {
          // Use the processed data even if processing "failed"
          return {
            success: true,
            newValue: processingResult.processedData,
            formatApplied: processingResult.formatApplied,
            errors: processingResult.errors,
            processingTime: performance.now() - startTime
          };
        } else {
          return {
            success: false,
            errors: processingResult.errors || ['Paste data processing failed'],
            processingTime: performance.now() - startTime
          };
        }
      }

      // Apply formatting if enabled and not already applied
      let finalValue = processingResult.processedData;
      let formatApplied = processingResult.formatApplied || false;

      if (config.autoFormat && !formatApplied && finalValue.length > 0) {
        const formatResult = await formatPastedValue(finalValue, fieldType);
        if (formatResult.success && formatResult.formattedValue !== finalValue) {
          finalValue = formatResult.formattedValue;
          formatApplied = true;
        }
      }

      // Validate if enabled
      let errors: string[] = processingResult.errors || [];
      if (config.validateOnPaste && finalValue.length > 0) {
        const validationResult = await validatePastedValue(finalValue, fieldType);
        if (validationResult.errors && validationResult.errors.length > 0) {
          errors = [...errors, ...validationResult.errors];
        }
      }

      // Prevent default paste behavior
      event.preventDefault();

      return {
        success: true,
        newValue: finalValue,
        formatApplied,
        errors: errors.length > 0 ? errors : undefined,
        processingTime: performance.now() - startTime
      };

    } catch (error) {
      return {
        success: false,
        errors: [`Paste processing error: ${error}`],
        processingTime: performance.now() - startTime
      };
    }
  };
}

/**
 * Process paste data using configured processors
 */
async function processPasteData(
  data: string,
  fieldType: FieldType,
  config: PasteProcessingConfig,
  processors: PasteDataProcessor[]
): Promise<PasteProcessingResult> {
  // Trim whitespace
  let processedData = data.trim();

  // Handle multi-line restriction
  if (!config.allowMultiLine && processedData.includes('\n')) {
    // Take only the first line
    processedData = processedData.split('\n')[0].trim();
  }

  // Check maximum length
  if (config.maxLength && processedData.length > config.maxLength) {
    processedData = processedData.substring(0, config.maxLength);
  }

  // Apply built-in processors in priority order
  const sortedProcessors = processors.sort((a, b) => b.priority - a.priority);
  
  for (const processor of sortedProcessors) {
    if (processor.pattern.test(processedData)) {
      try {
        const result = processor.process(processedData, fieldType);
        if (result.success) {
          return {
            ...result,
            detectedType: result.detectedType || processor.name
          };
        }
        // Continue to next processor if this one failed
      } catch (error) {
        // Continue to next processor on error
        console.warn(`Paste processor ${processor.name} failed:`, error);
      }
    }
  }

  // No processor handled the data - apply basic cleaning
  if (config.stripInvalidChars) {
    const cleanedData = cleanInvalidCharacters(processedData, fieldType);
    return {
      success: cleanedData.length > 0,
      processedData: cleanedData,
      detectedType: 'cleaned-input'
    };
  }

  return {
    success: true,
    processedData,
    detectedType: 'raw-paste'
  };
}

/**
 * Clean invalid characters based on field type
 */
function cleanInvalidCharacters(data: string, fieldType: FieldType): string {
  switch (fieldType) {
    case FieldType.PHONE:
      return data.replace(/[^0-9()\-\s]/g, '');
    
    case FieldType.ZIP:
      return data.replace(/[^0-9\-]/g, '');
    
    case FieldType.DATE_OF_BIRTH:
      return data.replace(/[^0-9\/\-]/g, '');
    
    case FieldType.CURRENCY:
      return data.replace(/[^0-9$,.\s]/g, '');
    
    case FieldType.SSN:
      return data.replace(/[^0-9\-]/g, '');
    
    case FieldType.NAME:
      // Remove numbers and special characters except typical name chars
      return data.replace(/[^a-zA-Z\s\-'.]/g, '').replace(/\s+/g, ' ');

    default:
      return data;
  }
}

/**
 * Format pasted value
 */
async function formatPastedValue(
  value: string,
  fieldType: FieldType
): Promise<{
  success: boolean;
  formattedValue: string;
  errors?: string[];
}> {
  try {
    // This will be integrated with the format pattern library (Issue #30)
    // For now, use the same formatting as input handler
    
    switch (fieldType) {
      case FieldType.PHONE:
        const digits = value.replace(/\D/g, '');
        if (digits.length === 10) {
          return {
            success: true,
            formattedValue: `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
          };
        }
        break;

      case FieldType.ZIP:
        const zipDigits = value.replace(/\D/g, '');
        if (zipDigits.length === 9) {
          return {
            success: true,
            formattedValue: `${zipDigits.slice(0, 5)}-${zipDigits.slice(5)}`
          };
        } else if (zipDigits.length === 5) {
          return {
            success: true,
            formattedValue: zipDigits
          };
        }
        break;

      case FieldType.DATE_OF_BIRTH:
        const dateDigits = value.replace(/\D/g, '');
        if (dateDigits.length === 8) {
          return {
            success: true,
            formattedValue: `${dateDigits.slice(0, 2)}/${dateDigits.slice(2, 4)}/${dateDigits.slice(4)}`
          };
        }
        break;

      case FieldType.CURRENCY:
        const num = parseFloat(value.replace(/[^\d.]/g, ''));
        if (!isNaN(num)) {
          return {
            success: true,
            formattedValue: `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          };
        }
        break;

      case FieldType.NAME:
        // Trim whitespace and capitalize first letter of each word
        const formattedName = value.trim()
          .toLowerCase()
          .replace(/(?:^|\s|-|')\S/g, (c) => c.toUpperCase());
        return {
          success: true,
          formattedValue: formattedName
        };

      default:
        break;
    }

    return { success: true, formattedValue: value };

  } catch (error) {
    return {
      success: false,
      formattedValue: value,
      errors: [`Formatting error: ${error}`]
    };
  }
}

/**
 * Validate pasted value
 */
async function validatePastedValue(
  value: string,
  fieldType: FieldType
): Promise<{ errors?: string[] }> {
  const errors: string[] = [];

  // Basic validation based on field type
  switch (fieldType) {
    case FieldType.PHONE:
      const phoneDigits = value.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        errors.push('Phone number must be 10 digits');
      }
      break;

    case FieldType.ZIP:
      const zipDigits = value.replace(/\D/g, '');
      if (zipDigits.length !== 5 && zipDigits.length !== 9) {
        errors.push('ZIP code must be 5 or 9 digits');
      }
      break;

    case FieldType.DATE_OF_BIRTH:
      // Basic date validation
      const dateMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (dateMatch) {
        const [, month, day, year] = dateMatch;
        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);
        const yearNum = parseInt(year, 10);
        
        if (monthNum < 1 || monthNum > 12) {
          errors.push('Invalid month');
        }
        if (dayNum < 1 || dayNum > 31) {
          errors.push('Invalid day');
        }
        if (yearNum < 1900 || yearNum > new Date().getFullYear()) {
          errors.push('Invalid year');
        }
      } else {
        errors.push('Date must be in MM/DD/YYYY format');
      }
      break;

    case FieldType.SSN:
      const ssnDigits = value.replace(/\D/g, '');
      if (ssnDigits.length !== 9) {
        errors.push('SSN must be 9 digits');
      }
      break;

    case FieldType.NAME:
      if (value.trim().length === 0) {
        errors.push('Name cannot be empty');
      } else if (value.includes(' ')) {
        // This validates if full name was pasted, but individual first/last name fields shouldn't have spaces
        // We'll rely on the field-specific validation which is stricter for first/last name
        errors.push('Spaces are not allowed in this field');
      }
      break;

    default:
      break;
  }

  return { errors: errors.length > 0 ? errors : undefined };
}

/**
 * Get default paste configuration for field type
 */
export function getDefaultPasteConfig(fieldType: FieldType): PasteProcessingConfig {
  return { ...DEFAULT_PASTE_CONFIGS[fieldType] };
}

/**
 * Register custom paste processor
 */
export function registerPasteProcessor(processor: PasteDataProcessor): void {
  BUILT_IN_PROCESSORS.push(processor);
  // Sort by priority to maintain correct order
  BUILT_IN_PROCESSORS.sort((a, b) => b.priority - a.priority);
}

/**
 * Check if field type supports paste formatting
 */
export function supportsPasteFormatting(fieldType: FieldType): boolean {
  return DEFAULT_PASTE_CONFIGS[fieldType]?.autoFormat || false;
}